// @spec SHL-STR-039
import { MessageSquare, RotateCcw, ServerCrash } from 'lucide-react';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

export interface ServerErrorProps {
  /** Optional error object (not displayed, only for logging) */
  error?: Error;
  /** Optional custom title (default: "Something went wrong") */
  title?: string;
  /** Optional custom description */
  description?: string;
  /** Optional callback for "Report Issue" button */
  onReportIssue?: (errorId: string) => void;
  /** Optional callback for custom retry logic */
  onRetry?: () => void;
  /** Hide retry button */
  hideRetry?: boolean;
  /** Hide report issue button */
  hideReportIssue?: boolean;
}

/**
 * Generates a unique error ID for support reference.
 * Format: ERR-{base36timestamp}-{random4char}
 * Example: ERR-m5kj8p2-a7b3
 */
function generateErrorId(): string {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2, 6);
  return `ERR-${timestamp}-${random}`;
}

export function ServerError({
  error,
  title,
  description,
  onReportIssue,
  onRetry,
  hideRetry,
  hideReportIssue,
}: ServerErrorProps) {
  const { t } = useTranslation();
  const retryButtonRef = useRef<HTMLButtonElement>(null);

  // Use i18n translations as defaults, allowing prop overrides
  const displayTitle = title ?? t('errors.somethingWentWrong');
  const displayDescription = description ?? t('errors.somethingWentWrongDescription');

  // Generate error ID once on mount (stable across re-renders)
  const errorId = useMemo(() => generateErrorId(), []);

  // Log error on mount
  useEffect(() => {
    console.error('[500]', {
      errorId,
      path: window.location.pathname,
      timestamp: new Date().toISOString(),
      error: error?.message ?? null,
      stack: error?.stack ?? null,
    });
  }, [errorId, error]);

  // Focus retry button on mount for accessibility
  useEffect(() => {
    if (!hideRetry) {
      retryButtonRef.current?.focus();
    }
  }, [hideRetry]);

  const handleRetry = () => {
    if (onRetry) {
      onRetry();
    } else {
      window.location.reload();
    }
  };

  const handleReportIssue = () => {
    if (onReportIssue) {
      onReportIssue(errorId);
    } else {
      // Navigate to support page with error ID
      window.location.href = `/support?errorId=${encodeURIComponent(errorId)}`;
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4" role="alert">
      <Card className="w-full max-w-md text-center">
        <CardHeader>
          <div className="mx-auto mb-4">
            <ServerCrash className="h-16 w-16 text-muted-foreground" aria-hidden="true" />
          </div>
          <CardTitle className="text-2xl">{displayTitle}</CardTitle>
          <CardDescription className="text-base">{displayDescription}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Error ID for support reference */}
          <p className="text-sm text-muted-foreground">{t('errors.errorId', { errorId })}</p>

          {/* Action buttons */}
          <div className="flex flex-col gap-2 sm:flex-row sm:justify-center">
            {!hideRetry && (
              <Button ref={retryButtonRef} onClick={handleRetry}>
                <RotateCcw className="mr-2 h-4 w-4" />
                {t('buttons.retry')}
              </Button>
            )}
            {!hideReportIssue && (
              <Button variant="outline" onClick={handleReportIssue}>
                <MessageSquare className="mr-2 h-4 w-4" />
                {t('buttons.reportIssue')}
              </Button>
            )}
          </div>

          {/* Support notification */}
          <div className="border-t pt-4">
            <p className="text-sm text-muted-foreground">{t('errors.errorLogged')}</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
